import { useMemo } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { AlertTriangle } from 'lucide-react';
import { useEventosAcesso } from '../../api/hooks/useEventosAcesso';
import type { PresencaDiaria } from '../../types/models';

interface TurmaDia {
  id: string;
  nome: string;
  pct: number | null;
  temRegistro?: boolean;
}

interface Props {
  selectedIso: string;
  hojeStr: string;
  alunosAtivos: number;
  presencas: PresencaDiaria[];
  turmasStatus: TurmaDia[];
  loading?: boolean;
  /** Alinha ao fallback do WS no dashboard (false = sem poll). */
  refetchIntervalMs?: number | false;
  /**
   * Quando o dia selecionado é o operacional, usa a mesma contagem da Torre
   * (evita divergência Panorama 51 vs KPI após limpeza/refetch).
   */
  portariaCount?: number;
}

function formatDiaCurto(iso: string) {
  return new Date(`${iso}T12:00:00`).toLocaleDateString('pt-BR', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

const tooltipStyle = {
  fontSize: 10,
  fontWeight: 500,
  borderRadius: 6,
  border: '1px solid #e8ecf1',
};

export function AdminCalendarDayPanel({
  selectedIso,
  hojeStr,
  alunosAtivos,
  presencas,
  loading,
  refetchIntervalMs,
  portariaCount,
}: Props) {
  const isHoje = selectedIso === hojeStr;
  const { eventos, isLoading: loadingEventos } = useEventosAcesso({
    data: selectedIso,
    refetchIntervalMs: refetchIntervalMs ?? 30_000,
  });

  const presentes = presencas.filter(
    (p) => p.status === 'PRESENTE' || p.status === 'ATRASADO',
  ).length;
  const ausentes = presencas.filter((p) => p.status === 'AUSENTE').length;
  const temFrequenciaNoDia = presencas.length > 0;
  /** Sempre numérico: sem chamada = todos aguardam registro. */
  const semRegistro = Math.max(0, alunosAtivos - presentes - ausentes);
  const inconsistencias = presencas.filter(
    (p) => p.inconsistencia || (p.entrouNaEscola && p.status === 'AUSENTE'),
  ).length;
  /** % da escola (mesmo critério da Torre), não só entre lançados. */
  const pctEscola =
    temFrequenciaNoDia && alunosAtivos > 0
      ? Math.round((presentes / alunosAtivos) * 100)
      : null;
  const portaria =
    typeof portariaCount === 'number' && isHoje ? portariaCount : eventos.length;
  const busy = loading || (typeof portariaCount !== 'number' && loadingEventos);

  const lineData = useMemo(
    () => [
      { rotulo: 'Pres.', valor: presentes, cor: '#10b981' },
      { rotulo: 'Aus.', valor: ausentes, cor: '#f59e0b' },
      { rotulo: 'S/reg', valor: semRegistro, cor: '#94a3b8' },
      { rotulo: 'Port.', valor: portaria, cor: '#2563eb' },
    ],
    [presentes, ausentes, semRegistro, portaria],
  );

  const resumo = !busy
    ? !temFrequenciaNoDia
      ? portaria > 0
        ? `Sem chamada no dia · ${portaria} RFID na portaria`
        : 'Sem movimento no dia'
      : semRegistro > 0
        ? `Presença ${pctEscola}% no dia · escola · ${presentes}/${alunosAtivos}`
        : `Presença ${pctEscola}% no dia · ${alunosAtivos} alunos da escola`
    : 'Carregando…';

  return (
    <div className="adm-desk-day-panel adm-desk-day-panel--escola">
      <div className="adm-desk-day-panel-top">
        <h4 className="adm-desk-day-panel-label">Panorama do dia</h4>
        <span className="adm-desk-day-panel-date">{formatDiaCurto(selectedIso)}</span>
        {isHoje ? (
          <span className="adm-desk-day-panel-live">Dia operacional</span>
        ) : (
          <span className="adm-desk-day-panel-live adm-desk-day-panel-live--hist">Histórico</span>
        )}
      </div>

      <div className="adm-desk-day-strip" aria-label="Indicadores do dia (escola)">
        <div className="adm-desk-day-chip adm-desk-day-chip--green">
          <span className="adm-desk-day-chip-val">{busy ? '…' : presentes}</span>
          <span className="adm-desk-day-chip-lbl">Pres.</span>
        </div>
        <div className="adm-desk-day-chip adm-desk-day-chip--amber">
          <span className="adm-desk-day-chip-val">{busy ? '…' : ausentes}</span>
          <span className="adm-desk-day-chip-lbl">Aus.</span>
        </div>
        <div className="adm-desk-day-chip adm-desk-day-chip--slate">
          <span className="adm-desk-day-chip-val">{busy ? '…' : semRegistro}</span>
          <span className="adm-desk-day-chip-lbl">S/reg</span>
        </div>
        <div className="adm-desk-day-chip adm-desk-day-chip--blue">
          <span className="adm-desk-day-chip-val">{busy ? '…' : portaria}</span>
          <span className="adm-desk-day-chip-lbl">Port.</span>
        </div>
      </div>

      <div className="adm-desk-day-line-chart" aria-hidden={busy}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={lineData} margin={{ top: 2, right: 2, left: -32, bottom: -4 }}>
            <CartesianGrid strokeDasharray="2 4" vertical={false} stroke="#f1f5f9" />
            <XAxis
              dataKey="rotulo"
              tick={{ fontSize: 9, fill: '#94a3b8', fontWeight: 600 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis hide allowDecimals={false} />
            <Tooltip
              contentStyle={tooltipStyle}
              formatter={(v: any) => [v, '']}
              labelFormatter={(l) => l}
            />
            <Line
              type="monotone"
              dataKey="valor"
              stroke="#2563eb"
              strokeWidth={2}
              dot={{ r: 3, fill: '#2563eb', strokeWidth: 0 }}
              activeDot={{ r: 4 }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {inconsistencias > 0 && (
        <p className="adm-desk-day-warn">
          <AlertTriangle size={11} />
          {inconsistencias} inconsist.
        </p>
      )}

      <p className="adm-desk-day-meta-line">{resumo}</p>
    </div>
  );
}
